import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { AcrScanStatus, AcrScanResult } from "../../types/acr";
import type { ImageBoardItem, ImageBoardStroke } from "./imageBoard";
import type { NoteDisplayStyle } from "./notes";

// 型定義

// ファイルダウンロード済みユーザー情報
export interface DownloadedUser {
  id: number;
  display_name: string;
  avatar: string | null;
  downloaded_at?: string;
}

export interface ProjectMessageUser {
  id: number;
  display_name: string;
  avatar: string | null;
  position: "CLIENT" | "REVIEWER" | "ADMIN" | "PRODUCER" | "SPECIALIST" | "DIRECTOR";
  seen_at?: string; // 既読日時（ISO 8601形式）
}

// 添付ファイルの型定義（バックエンドAPIのフィールド名に合わせる）
export interface ProjectMessageFile {
  id: string; // ファイルID
  file_key?: string; // S3キー（更新 API で既存ファイルを保持する際に使用）
  original_url: string; // オリジナルファイルURL（プレビュー/再生用）
  download_url?: string | null; // ダウンロード用 presigned URL（attachment 付き・短期有効。DL時はこちらを優先）
  file_name: string; // ファイル名
  file_type: "image" | "video" | "audio" | "document" | "other"; // ファイルタイプ
  file_metadata?: string; // ファイルメタデータ（JSON文字列）
  folder?: {
    id: string;
    name: string;
    parent_id: string | null;
  };
  downloaded_users?: DownloadedUser[];
  updated_at?: string; // ファイル自体の更新日時（リネーム・差し替えで更新される）
  is_shared?: boolean; // 有効な共有リンクが存在するか（共有中バッジ用）
  optimized_url?: string | null; // 最適化済みURL（m4a等）
  waveform_url?: string | null; // 波形JSONファイルURL
  has_waveform?: boolean; // 波形データの有無
  thumbnail?: string | null; // サムネイル画像URL（動画フレーム / PDF 1ページ目）
  acr_scan_status?: AcrScanStatus; // ACR楽曲認識ステータス
  acr_scan_result?: AcrScanResult | null; // ACR楽曲認識結果
  acr_scanned_at?: string | null; // ACR Cloud送信成功日時（processing遷移時にセット）
}

// AI 校正（チクチクフィルター）/ 返信生成（返信君）の元/AI 両バージョン（投稿者本人のみ）。
export interface ProjectMessageProofread {
  original: string;
  corrected: string;
  active: "original" | "ai";
}

// 送信/更新時に渡す proofread ペイロード（message 本文には常にアクティブ側が入る）。
export interface ProjectMessageProofreadPayload {
  original_comment?: string | null;
  ai_comment?: string | null;
  active_variant?: "original" | "ai" | null;
}

// メッセージに紐づくフォルダ情報（空フォルダ含む。ファイル一覧のツリー表示用）
export interface ProjectMessageFolder {
  id: string;
  name: string;
  parent_id: string | null;
  is_shared?: boolean; // 有効な共有リンクが存在するか（共有中バッジ用）
}

export interface ProjectMessage {
  id: string; // UUID
  author: ProjectMessageUser; // メッセージ作成者
  parent_id: string | null; // 親メッセージID（返信の場合）
  message: string; // メッセージ本文
  is_resolved: boolean; // 解決済みフラグ
  has_files: boolean; // ファイル添付フラグ
  /** トークルーム非表示の裏方メッセージ（受領ファイルの直接アップロード置き場）。
   *  タイムラインには描画せず、ファイル一覧では受領扱いで表示する */
  is_hidden?: boolean;
  files: ProjectMessageFile[]; // 添付ファイル配列
  folders?: ProjectMessageFolder[]; // 紐づく全フォルダ（空フォルダ含む）
  receivers: ProjectMessageUser[]; // 受信者リスト
  proofread?: ProjectMessageProofread | null; // AI 校正/返信生成の両バージョン（投稿者のみ）
  created_at: string; // 作成日時
  updated_at: string; // 更新日時
}

export interface ProjectMessagesData {
  messages: ProjectMessage[];
  count: number; // 総メッセージ数
  has_next: boolean; // 次のページがあるか
  next_offset: number | null; // 次のオフセット
  limit: number; // 取得件数の制限
  offset: number; // 現在のオフセット
  filter?: string; // 適用されたフィルター
  search?: string | null; // 検索語
}

export interface ProjectMessagesResponse {
  status: string;
  data: ProjectMessagesData;
}

export interface GetProjectMessagesQueryParams {
  project_id: string;
  limit?: number;
  offset?: number;
  filter?: string;
}

// 単体取得（WS 受信 created/updated で REST 追い取得に利用）
export interface GetProjectMessageDetailParams {
  project_id: string;
  message_id: string;
}

export interface GetProjectMessageDetailResponse {
  status: string;
  data: ProjectMessage;
}

// メッセージ送信用の型定義
export interface SendProjectMessageParams extends ProjectMessageProofreadPayload {
  project_id: string;
  message: string;
  parent_id?: string | null;
  file_keys?: string[];  // アップロード済みファイルのキー配列
}

export interface SendProjectMessageResponse {
  status: string;
  data: ProjectMessage;
}

// メッセージ削除用の型定義
export interface DeleteProjectMessageParams {
  project_id: string;
  message_id: string;
}

export interface DeleteProjectMessageResponse {
  status: string;
  message: string;
}

// メッセージ更新用の型定義
export interface UpdateProjectMessageParams extends ProjectMessageProofreadPayload {
  project_id: string;
  message_id: string;
  message?: string;
  is_resolved?: boolean;
  file_keys?: string[];
}

export interface UpdateProjectMessageResponse {
  status: string;
  data: {
    message: ProjectMessage;
  };
}

// 添付ファイル更新（リネーム / 差し替え / フォルダ移動）用の型定義
export interface UpdateProjectMessageFileParams {
  project_id: string;
  file_id: string;
  /** 新しい表示名（リネーム） */
  file_name?: string;
  /** アップロード済みの新しいS3キー（差し替え） */
  file_key?: string;
  /** 移動先フォルダID（同一メッセージ内のフォルダのみ） */
  folder_id?: string;
}

export interface UpdateProjectMessageFileResponse {
  status: string;
  data: {
    id: string;
    file_key: string | null;
    original_url: string | null;
    file_name: string;
    file_type: ProjectMessageFile["file_type"];
    file_metadata?: string;
    folder?: ProjectMessageFile["folder"] | null;
    updated_at: string;
  };
}

// 添付ファイル削除用の型定義
export interface DeleteProjectMessageFileParams {
  project_id: string;
  file_id: string;
}

export interface DeleteProjectMessageFileResponse {
  status: string;
  data: {
    deleted_file_id: string;
    message_id: string | null;
  };
}

// 既存フォルダへのファイル追加（外部D&Dアップロード）用の型定義
export interface AddProjectMessageFileParams {
  project_id: string;
  /** アップロード済みのS3キー */
  file_key: string;
  /** 追加先フォルダID（hidden アップロード時は省略） */
  folder_id?: string;
  /**
   * true でトークルーム非表示の裏方メッセージへ添付する
   * （ファイル一覧「受領ファイル」への直接アップロード。プロデューサー陣営のみ。
   *  メール通知・未読/未DLバッジの対象にならない）
   */
  hidden?: boolean;
  /**
   * hidden=true のときのみ有効。裏方メッセージ配下に階層を作成して
   * 添付するフォルダパス（例: "素材集/BGM"）。フォルダごとの D&D
   * アップロードでサブフォルダ構造を保持するために使う
   */
  folder_path?: string;
}

export interface AddProjectMessageFileResponse {
  status: string;
  data: {
    id: string;
    message_id: string | null;
    file_key: string | null;
    original_url: string | null;
    file_name: string;
    file_type: ProjectMessageFile["file_type"];
    file_metadata?: string;
    folder?: ProjectMessageFolder | null;
    /**
     * folder_path で解決したフォルダのルート→最下層チェーン
     * （未使用時は空配列。onFolderCreated でローカル状態へ反映する）
     */
    folders?: ProjectMessageFolder[];
    updated_at: string;
  };
}

// フォルダ作成用の型定義（既存フォルダ内のみ）
export interface CreateProjectMessageFolderParams {
  project_id: string;
  parent_folder_id: string;
  name: string;
}

export interface CreateProjectMessageFolderResponse {
  status: string;
  data: {
    id: string;
    name: string;
    parent_id: string | null;
    message_id: string | null;
  };
}

// フォルダ削除用の型定義（空フォルダのみ）
export interface DeleteProjectMessageFolderParams {
  project_id: string;
  folder_id: string;
}

export interface DeleteProjectMessageFolderResponse {
  status: string;
  data: {
    deleted_folder_id: string;
    message_id: string | null;
  };
}

// 共有リンク発行用の型定義
export interface CreateShareLinkParams {
  project_id: string;
  file_id?: string;
  folder_id?: string;
  /** 有効期限（ISO日時 or YYYY-MM-DD。null = 無期限） */
  expires_at?: string | null;
  /** プロジェクトメンバーのみに制限（既定 true） */
  require_membership?: boolean;
}

export interface ShareLinkData {
  id: string;
  token: string;
  expires_at: string | null;
  require_membership: boolean;
}

export interface CreateShareLinkResponse {
  status: string;
  data: ShareLinkData;
}

// 共有リンク取得（現状確認）用の型定義
export interface GetShareLinkParams {
  project_id: string;
  file_id?: string;
  folder_id?: string;
}

export interface GetShareLinkResponse {
  status: string;
  data: ShareLinkData | null;
}

// 共有リンクアクセス（共有ページ）用の型定義
export interface SharedContentFile {
  id: string;
  name: string;
  /** フォルダ共有時の相対パス（ファイル共有時はファイル名と同じ） */
  path: string;
  file_type: string | null;
  size: number | null;
  url: string | null;
}

interface SharedContentBase {
  name: string;
  /** 出所の表示用（トークルーム = プロジェクト名、ライブラリ / ノート = セクション名） */
  project_name: string | null;
  expires_at: string | null;
  require_membership: boolean;
}

/** トークルーム / 共有ライブラリのファイル・フォルダ共有（ダウンロード） */
export interface SharedFilesContent extends SharedContentBase {
  type: "file" | "folder";
  files: SharedContentFile[];
}

/** 共有ノートのフォルダ共有内のフォルダ（parent_id: null = 共有したフォルダ直下） */
export interface SharedNoteFolder {
  id: string;
  title: string;
  parent_id: string | null;
  sort_order: number;
}

/** 共有ノートのフォルダ共有内のイメージボードの中身（作成者は含まない） */
export interface SharedBoard {
  items: Omit<ImageBoardItem, "created_by">[];
  strokes: Omit<ImageBoardStroke, "created_by">[];
}

/** 共有ノートのフォルダ共有内のページ（閲覧専用。folder_id: null = 共有したフォルダ直下） */
export interface SharedNote {
  id: string;
  title: string;
  folder_id: string | null;
  display_style: NoteDisplayStyle;
  category: string;
  content: string;
  sort_order: number;
  modified: string | null;
  /** display_style が board のときだけ */
  board?: SharedBoard;
}

/** 共有ノートのフォルダ共有（閲覧専用ビューア） */
export interface SharedNoteFolderContent extends SharedContentBase {
  type: "note_folder";
  folders: SharedNoteFolder[];
  notes: SharedNote[];
}

export type SharedContent = SharedFilesContent | SharedNoteFolderContent;

export interface SharedContentResponse {
  status: string;
  data: SharedContent;
}

// 複数メッセージの既読登録用の型定義
export interface MarkMessagesAsReadParams {
  project_id: string;
  message_ids: string[];
}

export interface MarkMessagesAsReadResponse {
  status: string;
  data: {
    read_message_ids: string[];
  };
}

// ファイルダウンロード記録用の型定義
export interface MarkFilesAsDownloadedParams {
  project_id: string;
  downloaded_file_ids: string[];
}

export interface MarkFilesAsDownloadedResponse {
  status: string;
  data: {
    downloaded_file_ids: string[];
  };
}

// トークルームのバッジ数（未読数・未ダウンロード数）取得用の型定義
export interface TalkroomBadgeCountsParams {
  project_id: string;
}

export interface TalkroomBadgeCountsResponse {
  status: string;
  data: {
    unread_message_count: number;
    undownload_file_count: number;
  };
}

// Define a service using a base URL and expected endpoints
export const projectMessagesApi = createApi({
  reducerPath: "projectMessagesApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["ProjectMessages", "TalkroomBadgeCounts"],
  endpoints: (builder) => ({
    getProjectMessages: builder.query<ProjectMessagesResponse, GetProjectMessagesQueryParams>({
      query: ({ project_id, limit = 20, offset = 0, filter }) => {
        const params: Record<string, string> = {
          limit: limit.toString(),
          offset: offset.toString(),
        };
        
        if (filter) {
          params.filter = filter;
        }
        
        const queryString = new URLSearchParams(params).toString();
        return `/api/${project_id}/project_messages?${queryString}`;
      },
      providesTags: ["ProjectMessages"],
      keepUnusedDataFor: 30, // 30秒間キャッシュを保持してUX向上
    }),
    // 単体取得（WebSocket created/updated 受信時に差分を追い取得する用途）
    getProjectMessageDetail: builder.query<
      GetProjectMessageDetailResponse,
      GetProjectMessageDetailParams
    >({
      query: ({ project_id, message_id }) =>
        `/api/${project_id}/project_messages/${message_id}`,
      keepUnusedDataFor: 0,
    }),
    // メッセージ送信用のmutation
    sendProjectMessage: builder.mutation<SendProjectMessageResponse, SendProjectMessageParams>({
      query: ({ project_id, message, parent_id, file_keys, original_comment, ai_comment, active_variant }) => ({
        url: `/api/${project_id}/project_messages`,
        method: "POST",
        body: {
          message,
          ...(parent_id && { parent_id }),
          ...(file_keys && file_keys.length > 0 && { file_keys }),
          ...(original_comment !== undefined && { original_comment }),
          ...(ai_comment !== undefined && { ai_comment }),
          ...(active_variant !== undefined && { active_variant }),
        },
      }),
      // キャッシュ無効化は行わず、TalkRoom側で手動で最新メッセージを取得・マージする
    }),
    // メッセージ削除用のmutation
    deleteProjectMessage: builder.mutation<DeleteProjectMessageResponse, DeleteProjectMessageParams>({
      query: ({ project_id, message_id }) => ({
        url: `/api/${project_id}/project_messages/${message_id}`,
        method: "DELETE",
      }),
    }),
    // メッセージ更新用のmutation
    updateProjectMessage: builder.mutation<UpdateProjectMessageResponse, UpdateProjectMessageParams>({
      query: ({ project_id, message_id, message, is_resolved, file_keys, original_comment, ai_comment, active_variant }) => ({
        url: `/api/${project_id}/project_messages/${message_id}`,
        method: "PATCH",
        body: {
          ...(message !== undefined && { message }),
          ...(is_resolved !== undefined && { is_resolved }),
          ...(file_keys !== undefined && { file_keys }),
          ...(original_comment !== undefined && { original_comment }),
          ...(ai_comment !== undefined && { ai_comment }),
          ...(active_variant !== undefined && { active_variant }),
        },
      }),
    }),
    // 添付ファイルの更新（リネーム / 差し替え / フォルダ移動）用mutation
    updateProjectMessageFile: builder.mutation<
      UpdateProjectMessageFileResponse,
      UpdateProjectMessageFileParams
    >({
      query: ({ project_id, file_id, file_name, file_key, folder_id }) => ({
        url: `/api/${project_id}/project_messages/files/${file_id}`,
        method: "PATCH",
        body: {
          ...(file_name !== undefined && { file_name }),
          ...(file_key !== undefined && { file_key }),
          ...(folder_id !== undefined && { folder_id }),
        },
      }),
      invalidatesTags: ["ProjectMessages"],
    }),
    // 添付ファイルの削除用mutation
    deleteProjectMessageFile: builder.mutation<
      DeleteProjectMessageFileResponse,
      DeleteProjectMessageFileParams
    >({
      query: ({ project_id, file_id }) => ({
        url: `/api/${project_id}/project_messages/files/${file_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProjectMessages"],
    }),
    // 既存フォルダ（または hidden = 受領ファイル置き場）へのファイル追加
    // （外部D&Dアップロード）用mutation
    addProjectMessageFile: builder.mutation<
      AddProjectMessageFileResponse,
      AddProjectMessageFileParams
    >({
      query: ({ project_id, file_key, folder_id, hidden, folder_path }) => ({
        url: `/api/${project_id}/project_messages/files`,
        method: "POST",
        body: {
          file_key,
          ...(folder_id !== undefined && { folder_id }),
          ...(hidden !== undefined && { hidden }),
          ...(folder_path !== undefined && { folder_path }),
        },
      }),
      invalidatesTags: ["ProjectMessages"],
    }),
    // フォルダ作成用mutation（既存フォルダ内のみ）
    createProjectMessageFolder: builder.mutation<
      CreateProjectMessageFolderResponse,
      CreateProjectMessageFolderParams
    >({
      query: ({ project_id, parent_folder_id, name }) => ({
        url: `/api/${project_id}/project_messages/folders`,
        method: "POST",
        body: { parent_folder_id, name },
      }),
      invalidatesTags: ["ProjectMessages"],
    }),
    // フォルダ削除用mutation（空フォルダのみ）
    deleteProjectMessageFolder: builder.mutation<
      DeleteProjectMessageFolderResponse,
      DeleteProjectMessageFolderParams
    >({
      query: ({ project_id, folder_id }) => ({
        url: `/api/${project_id}/project_messages/folders/${folder_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProjectMessages"],
    }),
    // 共有リンクの現状取得用query（無ければ data: null）
    getShareLink: builder.query<GetShareLinkResponse, GetShareLinkParams>({
      query: ({ project_id, file_id, folder_id }) => {
        const params = new URLSearchParams();
        if (file_id) params.set("file_id", file_id);
        if (folder_id) params.set("folder_id", folder_id);
        return `/api/${project_id}/project_messages/share_links?${params.toString()}`;
      },
      keepUnusedDataFor: 0,
    }),
    // 共有リンク発行/更新用mutation（対象ごとに1本。同じトークンを更新）
    createShareLink: builder.mutation<CreateShareLinkResponse, CreateShareLinkParams>({
      query: ({ project_id, file_id, folder_id, expires_at, require_membership }) => ({
        url: `/api/${project_id}/project_messages/share_links`,
        method: "POST",
        body: {
          ...(file_id !== undefined && { file_id }),
          ...(folder_id !== undefined && { folder_id }),
          expires_at: expires_at ?? null,
          require_membership: require_membership ?? true,
        },
      }),
      invalidatesTags: ["ProjectMessages"],
    }),
    // 共有解除用mutation
    deleteShareLink: builder.mutation<{ status: string }, GetShareLinkParams>({
      query: ({ project_id, file_id, folder_id }) => {
        const params = new URLSearchParams();
        if (file_id) params.set("file_id", file_id);
        if (folder_id) params.set("folder_id", folder_id);
        return {
          url: `/api/${project_id}/project_messages/share_links?${params.toString()}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["ProjectMessages"],
    }),
    // 共有リンクアクセス（共有ページ）用query
    getSharedContent: builder.query<SharedContentResponse, { token: string }>({
      query: ({ token }) => `/api/share/${token}`,
      keepUnusedDataFor: 0,
    }),
    // 複数メッセージの既読登録用mutation
    // PATCHでmessage_idsを送信し、BE側で既読処理を実行
    markMessagesAsRead: builder.mutation<MarkMessagesAsReadResponse, MarkMessagesAsReadParams>({
      query: ({ project_id, message_ids }) => ({
        url: `/api/${project_id}/project_messages`,
        method: "PATCH",
        body: { message_ids },
      }),
      // 既読登録後、BottomAppBar のバッジ数（未読）も再取得する
      invalidatesTags: ["ProjectMessages", "TalkroomBadgeCounts"],
    }),
    // ファイルダウンロード記録用mutation
    markFilesAsDownloaded: builder.mutation<MarkFilesAsDownloadedResponse, MarkFilesAsDownloadedParams>({
      query: ({ project_id, downloaded_file_ids }) => ({
        url: `/api/${project_id}/project_messages`,
        method: "PATCH",
        body: { downloaded_file_ids },
      }),
      // ダウンロード記録後、BottomAppBar のバッジ数（未ダウンロード）も再取得する
      invalidatesTags: ["TalkroomBadgeCounts"],
    }),
    // トークルームのバッジ数（未読数・未ダウンロード数）を取得
    getTalkroomBadgeCounts: builder.query<TalkroomBadgeCountsResponse, TalkroomBadgeCountsParams>({
      query: ({ project_id }) => `/api/${project_id}/talkroom_badge_counts`,
      providesTags: ["TalkroomBadgeCounts"],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetProjectMessagesQuery,
  useLazyGetProjectMessagesQuery,
  useLazyGetProjectMessageDetailQuery,
  useSendProjectMessageMutation,
  useDeleteProjectMessageMutation,
  useUpdateProjectMessageMutation,
  useUpdateProjectMessageFileMutation,
  useDeleteProjectMessageFileMutation,
  useAddProjectMessageFileMutation,
  useCreateProjectMessageFolderMutation,
  useDeleteProjectMessageFolderMutation,
  useCreateShareLinkMutation,
  useDeleteShareLinkMutation,
  useLazyGetShareLinkQuery,
  useGetSharedContentQuery,
  useMarkMessagesAsReadMutation,
  useMarkFilesAsDownloadedMutation,
  useGetTalkroomBadgeCountsQuery,
} = projectMessagesApi;

